<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto bg-taupe-50"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-6 sm:pt-8 lg:pt-10 xl:max-w-7xl xl:mx-auto w-full"
        >
            <h1 id="primary-heading" class="sr-only">
                Admin: Projects: Environments: Settings
            </h1>
            <div class="px-4 sm:px-6 lg:px-8 pb-12 space-y-8">
                <div class="mb-6">
                    <environment-header-tabs
                        current="settings"
                        :project="project.name"
                        :project-key="project.key"
                        :environment="environment.name"
                        :environment-key="environment.key"
                    />
                </div>

                <div
                    class="flex items-center justify-between mb-6 min-h-[2.5rem]"
                >
                    <h2 class="text-2xl font-medium text-taupe-900">
                        Environment Settings
                    </h2>
                </div>

                <form @submit.prevent="save" class="space-y-8">
                    <div
                        class="rounded-xl bg-white shadow-sm border border-taupe-200 p-6 space-y-6 hover:shadow-md transition-shadow"
                    >
                        <div class="space-y-1">
                            <h3 class="text-lg font-medium text-taupe-900">
                                Environment Information
                            </h3>
                            <p class="text-sm text-taupe-600">
                                Update the configuration for this environment.
                            </p>
                        </div>

                        <div class="border-t border-taupe-200 pt-6 space-y-6">
                            <div class="space-y-2">
                                <label
                                    for="name"
                                    class="block text-sm font-medium text-taupe-900"
                                    >Environment Name</label
                                >
                                <p class="text-sm text-taupe-600">
                                    The display name of your environment.
                                </p>
                                <input
                                    type="text"
                                    v-model="data.name"
                                    name="name"
                                    id="name"
                                    autocomplete="name"
                                    placeholder="Production"
                                    class="block w-full max-w-xl px-4 py-2.5 rounded-lg border border-taupe-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                                />
                            </div>

                            <div class="space-y-2">
                                <label
                                    class="block text-sm font-medium text-taupe-900"
                                    >Environment Key</label
                                >
                                <p class="text-sm text-taupe-600">
                                    The unique identifier for this environment
                                    (read-only).
                                </p>
                                <div
                                    class="block w-full max-w-xl px-4 py-2.5 rounded-lg border border-taupe-200 bg-taupe-50 text-taupe-700 font-mono text-sm"
                                >
                                    {{ data.key }}
                                </div>
                            </div>

                            <div class="space-y-2">
                                <label
                                    for="description"
                                    class="block text-sm font-medium text-taupe-900"
                                    >Description</label
                                >
                                <p class="text-sm text-taupe-600">
                                    A brief description of this environment
                                    (optional).
                                </p>
                                <textarea
                                    v-model="data.description"
                                    name="description"
                                    id="description"
                                    rows="3"
                                    placeholder="Describe this environment..."
                                    class="block w-full max-w-xl px-4 py-2.5 rounded-lg border border-taupe-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                                ></textarea>
                            </div>

                            <div class="space-y-2">
                                <label
                                    for="color"
                                    class="block text-sm font-medium text-taupe-900"
                                    >Color</label
                                >
                                <p class="text-sm text-taupe-600">
                                    Choose a color to help identify this
                                    environment.
                                </p>
                                <div class="mt-2">
                                    <v-swatches
                                        v-model="data.color"
                                        inline
                                        class="max-w-md"
                                    ></v-swatches>
                                </div>
                            </div>

                            <div
                                class="space-y-2 pt-4 border-t border-taupe-200"
                            >
                                <label
                                    class="block text-sm font-medium text-taupe-900"
                                    >Safety Options</label
                                >
                                <p class="text-sm text-taupe-600">
                                    Configure additional safety checks for flag
                                    changes.
                                </p>
                                <div class="mt-3 space-y-3">
                                    <div class="flex items-start">
                                        <input
                                            type="checkbox"
                                            name="comments_required"
                                            id="comments-required"
                                            v-model="data.comments_required"
                                            true-value="1"
                                            false-value="0"
                                            class="mt-0.5 h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                                        />
                                        <label
                                            for="comments-required"
                                            class="ml-3 text-sm text-taupe-700"
                                        >
                                            Comment required when changing flag
                                            value
                                        </label>
                                    </div>
                                    <div class="flex items-start">
                                        <input
                                            type="checkbox"
                                            name="confirmation_required"
                                            id="confirmation-required"
                                            v-model="data.confirmation_required"
                                            true-value="1"
                                            false-value="0"
                                            class="mt-0.5 h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                                        />
                                        <label
                                            for="confirmation-required"
                                            class="ml-3 text-sm text-taupe-700"
                                        >
                                            Confirmation required when changing
                                            flag value
                                        </label>
                                    </div>
                                    <div class="flex items-start">
                                        <input
                                            type="checkbox"
                                            name="can_overlay"
                                            id="can-overlay"
                                            v-model="data.can_overlay"
                                            class="mt-0.5 h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                                        />
                                        <label
                                            for="can-overlay"
                                            class="ml-3 text-sm text-taupe-700"
                                        >
                                            Allow temporary flag overlays
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div
                                class="space-y-2 pt-4 border-t border-taupe-200"
                            >
                                <label
                                    class="block text-sm font-medium text-taupe-900"
                                    >Stale Flag Alerts</label
                                >
                                <p class="text-sm text-taupe-600">
                                    Get notified by email when too many flags
                                    haven't been evaluated recently.
                                </p>
                                <p
                                    v-if="
                                        !hasPlanFeature(
                                            'flag_lifecycle_management'
                                        )
                                    "
                                    class="text-sm text-taupe-500 italic"
                                >
                                    Stale flag alerts need a higher plan — no
                                    sales call, just upgrade in Billing.
                                </p>
                                <div v-else class="mt-3 space-y-3">
                                    <div class="flex items-start">
                                        <input
                                            type="checkbox"
                                            name="stale_flag_alerts_enabled"
                                            id="stale-flag-alerts-enabled"
                                            v-model="
                                                data.stale_flag_alerts_enabled
                                            "
                                            true-value="1"
                                            false-value="0"
                                            class="mt-0.5 h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                                        />
                                        <label
                                            for="stale-flag-alerts-enabled"
                                            class="ml-3 text-sm text-taupe-700"
                                        >
                                            Alert account admins when the stale
                                            flag count exceeds a threshold
                                        </label>
                                    </div>
                                    <div
                                        v-if="
                                            String(
                                                data.stale_flag_alerts_enabled
                                            ) === '1'
                                        "
                                        class="pl-7 space-y-2"
                                    >
                                        <label
                                            for="stale-flag-alert-threshold"
                                            class="block text-sm font-medium text-taupe-900"
                                            >Alert threshold</label
                                        >
                                        <p class="text-sm text-taupe-600">
                                            Send an alert once this many flags
                                            are stale.
                                        </p>
                                        <input
                                            type="number"
                                            min="1"
                                            v-model.number="
                                                data.stale_flag_alert_threshold
                                            "
                                            name="stale_flag_alert_threshold"
                                            id="stale-flag-alert-threshold"
                                            placeholder="10"
                                            class="block w-full max-w-xs px-4 py-2.5 rounded-lg border border-taupe-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex gap-3">
                        <zen-button
                            v-if="environmentChanged"
                            type="button"
                            variant="secondary"
                            @click="reset"
                        >
                            Cancel
                        </zen-button>
                        <zen-button
                            v-if="
                                can(
                                    PERMISSIONS.ENVIRONMENT_UPDATE,
                                    permissionScope
                                )
                            "
                            type="submit"
                            variant="primary"
                            :disabled="!environmentChanged"
                            :processing="saving"
                        >
                            Save Changes
                        </zen-button>
                    </div>
                </form>
            </div>
        </section>
    </main>
</template>

<script>
import { VSwatches } from 'vue3-swatches'
import 'vue3-swatches/dist/style.css'

import EnvironmentHeaderTabs from '@/screens/_components/admin/EnvironmentHeaderTabs.vue'
import { useSessionStore } from '@/stores/session'

import EnvironmentService from '@/services/project-environment.service.js'

import { errorMessage, successMessage } from '@/utils/notifications'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { usePlan } from '@/composables/usePlan'

export default {
    name: 'AdminProjectsEnvironmentsSettingsScreen',
    setup() {
        const { can } = usePermissions()
        const { hasPlanFeature } = usePlan()
        return { can, PERMISSIONS, hasPlanFeature }
    },
    components: {
        EnvironmentHeaderTabs,
        VSwatches,
    },
    data() {
        return {
            saving: false,
            data: {},
        }
    },
    computed: {
        sessionStore() {
            return useSessionStore()
        },
        environment() {
            return this.project.environments.data.filter(
                (environment) =>
                    environment.key === this.$route.params.environmentKey
            )[0]
        },
        project() {
            return this.sessionStore.data.projects.data.filter(
                (project) => project.key === this.$route.params.projectKey
            )[0]
        },
        environmentChanged() {
            return (
                JSON.stringify(this.data) !== JSON.stringify(this.environment)
            )
        },
        permissionScope() {
            return {
                projectUlid: this.project?.ulid,
                environmentUlid: this.environment?.ulid,
            }
        },
    },
    methods: {
        reset() {
            this.data = JSON.parse(JSON.stringify(this.environment))
        },
        save() {
            this.saving = true

            EnvironmentService.update(
                this.$route.params.projectKey,
                this.$route.params.environmentKey,
                this.data
            )
                .then((data) => {
                    this.sessionStore.upsertEnvironment(
                        this.$route.params.projectKey,
                        data.data
                    )
                    successMessage('The environment has been updated.')
                    this.reset()
                })
                .catch((error) => {
                    errorMessage(error)
                })
                .finally(() => {
                    this.saving = false
                })
        },
    },
    mounted() {
        this.reset()
    },
}
</script>
